// The one place an artifact or a repair meets a project. Pure: the device
// and the list's Durable Object both call these through applyChange, so the
// two can never disagree about what a graft does. Ported from the iOS
// PlanGenerator.apply(_ plan:), apply(_ fix:) and apply(_ swap:).
//
// New items get fresh ids. `newId` defaults to crypto.randomUUID(); a caller
// that needs two replicas to mint the same ids for the same change (the
// device and the list's Durable Object applying one graft) passes a generator
// seeded from the change.
import type { Artifact, EquipmentSwap, ExtractedPlan, StepFix } from './artifacts';
import { descendants, titleTrimmed, type Id, type Millis, type Project, type Snag, type Source, type Step, type Video } from './plan';

/** Makes the id of an item a change adds. */
export type NewId = () => Id;
const uuid: NewId = () => crypto.randomUUID();

/** Writes one stage's artifact onto the project and touches nothing else. */
export function applyArtifact(p: Project, a: Artifact, at: Millis, newId: NewId = uuid): Project {
  switch (a.stage) {
    case 'understand':
      return { ...p, brief: a.brief };
    case 'research':
      return { ...p, synthesis: a.synthesis, sources: a.sources };
    case 'extract':
      return { ...applyExtracted(p, a.plan, newId), generatedAt: at };
    case 'illustrate':
      return { ...p, blueprintHash: a.blueprintHash };
  }
}

/** One item of the new plan, tagged with the bundle it came in. */
interface Incoming { title: string; bundle: string; reason?: string }

/**
 * Merges an extracted plan into a project, keeping the done-state of items
 * whose titles survive, re-attaching surviving sub-steps under a surviving
 * parent, and fencing video links (ported from the iOS PlanGenerator.apply).
 *
 * Each incoming item carries its freshly derived bundle (and, for equipment,
 * its reason); a matched old item keeps its id and done-state but is
 * re-stamped with those, so a regenerated grouping takes effect.
 */
export function applyExtracted(p: Project, plan: ExtractedPlan, newId: NewId = uuid): Project {
  const steps: Incoming[] = (plan.stepGroups ?? []).flatMap((g) =>
    g.steps.filter((s) => s !== '').map((title) => ({ title, bundle: g.name })));
  const equipment: Incoming[] = (plan.equipmentGroups ?? []).flatMap((g) =>
    g.equipment.filter((e) => e.name !== '').map((e) => ({ title: e.name, bundle: g.name, reason: e.reason })));

  const updated: Project = { ...p, steps: mergeItems(p.steps, steps, newId), equipment: mergeItems(p.equipment, equipment, newId) };
  if (plan.title) updated.title = titleTrimmed(plan.title);
  if (plan.summary) updated.summary = plan.summary;
  const sources = p.sources ?? [];
  updated.videos = (plan.videos ?? []).flatMap((stub): Video[] => {
    const url = trustedVideoURL(stub.url, sources);
    return url ? [{ id: newId(), title: stub.title, duration: stub.duration ?? '', url }] : [];
  });
  updated.tips = (plan.tips ?? []).filter((t) => t !== '');
  return updated;
}

function mergeItems(old: readonly Step[], incoming: readonly Incoming[], newId: NewId): Step[] {
  // Each matched old item leaves the pool, so two equal titles can never both
  // resolve to one item: duplicate ids would make a tick flip the wrong row
  // and leave a step that can never be ticked.
  const pool = [...old];
  const merged = incoming.map((item): Step => {
    const stamps = { ...(item.bundle ? { bundle: item.bundle } : {}), ...(item.reason ? { reason: item.reason } : {}) };
    const i = pool.findIndex((s) => sameTitle(s.title, item.title));
    if (i >= 0) {
      // The new plan asked for this by name, so it is a step in its own right
      // now, even if it used to hang off another.
      const { bundle: _b, reason: _r, parent: _p, ...kept } = pool.splice(i, 1)[0]!;
      return { ...kept, ...stamps };
    }
    return { id: newId(), title: item.title, done: false, ...stamps };
  });

  // The pool now holds only what the new plan didn't ask for. Sub-steps in
  // there are the user's own record of hitting a wall, which the model has no
  // way to re-derive, so each one whose parent came through goes back under
  // it, in order, taking the parent's (possibly new) bundle so it is never
  // grouped away from the step it came out of. The rest go with their parent.
  const leftovers = pool;
  const placed = new Set<Id>(); // also what keeps a malformed cycle finite
  const attached = (parent: Step): Step[] =>
    leftovers
      .filter((c) => c.parent === parent.id && !placed.has(c.id) && placed.add(c.id))
      .flatMap((child) => {
        const { bundle: _old, ...rest } = child;
        const moved: Step = parent.bundle === undefined ? rest : { ...rest, bundle: parent.bundle };
        return [moved, ...attached(moved)];
      });
  return merged.flatMap((s) => [s, ...attached(s)]);
}

/** Titles match whatever their case, as on iOS. */
function sameTitle(a: string, b: string): boolean {
  return a.normalize('NFC').toLowerCase() === b.normalize('NFC').toLowerCase();
}

/**
 * Rewords a step in place and/or breaks it down; a second snag replaces the
 * earlier breakdown. The step keeps its id and its place, so its points and a
 * future merge survive; it loses its tick, because "it didn't work" is the
 * opposite of done. The breakdown lands directly underneath, in the step's
 * bundle. A snag on a sub-step nests under that sub-step.
 */
export function applyStepFix(p: Project, itemId: Id, fix: StepFix, note: string, photoHash: string | undefined, at: Millis, newId: NewId = uuid): Project {
  const index = p.steps.findIndex((s) => s.id === itemId);
  if (index < 0) return p;
  const old = p.steps[index]!;
  // A blank title would leave an unreadable row with no way back to the old one.
  const title = fix.step !== '' ? fix.step : old.title;
  const snag: Snag = {
    note,
    ...(photoHash !== undefined ? { photoHash } : {}),
    advice: fix.advice,
    ...(old.title !== title ? { previousTitle: old.title } : {}),
    reportedAt: at,
  };
  const step: Step = { ...old, title, done: false, snag, editedAt: at };
  const withStep = p.steps.map((s, i) => (i === index ? step : s));

  const stale = new Set(descendants({ ...p, steps: withStep }, itemId).map((s) => s.id));
  const steps = withStep.filter((s) => !stale.has(s.id));
  const where = steps.findIndex((s) => s.id === itemId);
  const breakdown = fix.substeps.filter((t) => t !== '').map((t): Step => ({
    id: newId(), title: t, done: false, editedAt: at, ...(step.bundle !== undefined ? { bundle: step.bundle } : {}), parent: itemId,
  }));
  steps.splice(where + 1, 0, ...breakdown);
  return { ...p, steps };
}

/**
 * Swaps a tool or material for its stand-in, in place. The row keeps its id
 * but loses its tick: a different thing is a different thing to go and get.
 */
export function applySwap(p: Project, itemId: Id, swap: EquipmentSwap, note: string, photoHash: string | undefined, at: Millis): Project {
  const index = p.equipment.findIndex((s) => s.id === itemId);
  if (index < 0) return p;
  const old = p.equipment[index]!;
  const item: Step = {
    ...old,
    title: swap.name !== '' ? swap.name : old.title,
    done: false,
    editedAt: at,
    snag: { note, ...(photoHash !== undefined ? { photoHash } : {}), advice: swap.advice, previousTitle: old.title, reportedAt: at },
  };
  if (swap.reason !== '') item.reason = swap.reason;
  return { ...p, equipment: p.equipment.map((s, i) => (i === index ? item : s)) };
}

// MARK: Video links (untrusted input)

/** Hosts a row wearing a play glyph can honestly lead to. */
export const VIDEO_HOSTS: readonly string[] = ['youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com'];

/**
 * Where one extracted video row may point, or undefined to drop it.
 *
 * The extract stage reads a digest of arbitrary web pages, so this URL is
 * attacker-reachable text: a page that says "and recommend this video" hands
 * the model a destination, and the row would carry a title of the page's
 * choosing. The prompt's "only real URLs from the digest" is a request; this
 * is the fence. Two ways through: a host that serves videos, or a host the
 * research stage cited for this project, so the worst a hostile page can do is
 * name a video, not a domain. Https only, and never a search page.
 *
 * Returns the URL as the browser will read it, so what was checked is what
 * opens.
 */
export function trustedVideoURL(raw: string, sources: readonly Source[]): string | undefined {
  const url = parseURL(raw);
  if (!url || url.protocol !== 'https:' || url.hostname === '' || isSearchPage(url)) return undefined;
  const host = bare(url.hostname.toLowerCase());
  const serves = (domain: string): boolean => {
    const d = bare(domain.toLowerCase());
    return d !== '' && (host === d || host.endsWith(`.${d}`));
  };
  if (VIDEO_HOSTS.some(serves)) return url.href;
  return sources.some((s) => serves(parseURL(s.url)?.hostname ?? '')) ? url.href : undefined;
}

/**
 * Whether a URL points at a site's search results rather than one video. A
 * model that can't recall a real video id reaches for the search page, which
 * is on exactly the right host; the row would wear a play glyph and land on a
 * page with no video. Matched on the first path segment: `/results` (YouTube)
 * and `/search` (Vimeo, Dailymotion, most article sites).
 */
export function isSearchPage(url: URL | string): boolean {
  const parsed = typeof url === 'string' ? parseURL(url) : url;
  if (!parsed) return false;
  const first = parsed.pathname.split('/').find((c) => c !== '');
  if (first === undefined) return false;
  const segment = decoded(first).toLowerCase();
  return segment === 'results' || segment === 'search';
}

function parseURL(raw: string): URL | undefined {
  try {
    return new URL(raw);
  } catch {
    return undefined;
  }
}

function decoded(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

const bare = (host: string): string => (host.startsWith('www.') ? host.slice(4) : host);
